"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.UndoManager = void 0;
const types_1 = require("./types");
const ot_1 = require("../engine/ot");
class UndoManager {
    constructor(resourceId) {
        this.resourceId = resourceId;
        this._undoStack = [];
        this._redoStack = [];
        this._maxLength = 50;
        this._commandManager = null;
        this._transformSquare = 100000;
    }
    setCommandManager(commandManager) {
        this._commandManager = commandManager;
    }
    undo() {
        const { _undoStack: undoStack, _commandManager: commandManager } = this;
        if (!undoStack.length) {
            return null;
        }
        const undoAction = undoStack.pop();
        if (undoAction && commandManager && commandManager.hasCommand(undoAction.cmd)) {
            return commandManager._executeActions(undoAction.cmd, undoAction.result, types_1.ExecuteType.Undo);
        }
        return null;
    }
    redo() {
        const { _redoStack: redoStack, _commandManager: commandManager } = this;
        if (!redoStack.length) {
            return null;
        }
        const redoAction = redoStack.pop();
        if (redoAction && commandManager && commandManager.hasCommand(redoAction.cmd)) {
            return commandManager._executeActions(redoAction.cmd, redoAction.result, types_1.ExecuteType.Redo);
        }
        return null;
    }
    static revertLinkedActions(cmd) {
        if (cmd.result.linkedActions) {
            cmd.result.linkedActions = cmd.result.linkedActions.map(lCmd => {
                return Object.assign(Object.assign({}, lCmd), { actions: ot_1.jot.invert(lCmd.actions) });
            });
        }
    }
    addUndoStack(command, executeType) {
        if (!command || !command.cmd) {
            return;
        }
        /**
         * The conversion of undo & redo stack needs to invert actions
         */
        if (executeType === types_1.ExecuteType.Undo) {
            const inverted = {
                cmd: command.cmd,
                result: Object.assign(Object.assign({}, command.result), { actions: ot_1.jot.invert(command.result.actions) }),
            };
            UndoManager.revertLinkedActions(inverted);
            this._redoStack.push(inverted);
        }
        else {
            const { _maxLength: maxLength, _undoStack: undoStack } = this;
            const deleteCount = undoStack.length - maxLength + 1;
            // clear the command that exceeds the stack size
            if (maxLength > 0 && deleteCount > 0) {
                undoStack.splice(0, deleteCount);
            }
            const inverted = {
                cmd: command.cmd,
                result: Object.assign(Object.assign({}, command.result), { actions: ot_1.jot.invert(command.result.actions) }),
            };
            UndoManager.revertLinkedActions(inverted);
            undoStack.push(inverted);
            // After executing the command, you need to clear the redo stack
            if (executeType === types_1.ExecuteType.Execute) {
                this._redoStack = [];
            }
        }
    }
    getStockLength(type) {
        if (type === 'undo') {
            return this._undoStack.length;
        }
        return this._redoStack.length;
    }
    getUndoStack() {
        return this._undoStack;
    }
    getRedoStack() {
        return this._redoStack;
    }
    /**
     * @description Temporary solution to solve the page stuck caused by excessive data volume
     * when transforming locally cached actions and remote collaborative actions
     * The current idea is to give up this transform if the product of the two actions of the transform
     * exceeds the set threshold. And clear all caches this time and later,
     * Subsequent solutions will solve performance problems through workers
     *
     * @param {number} stackActionLen
     * @param {number} remoteActionLen
     * @returns {boolean}
     * @private
     */
    checkTransformSquareOverLimit(stackActionLen, remoteActionLen) {
        return Boolean(stackActionLen * remoteActionLen > this._transformSquare);
    }
    transformStack(stack, remoteActions) {
        const newStack = [];
        for (let i = stack.length - 1; i >= 0; i--) {
            const stackActions = stack[i].result.actions;
            if (this.checkTransformSquareOverLimit(stackActions.length, remoteActions.length)) {
                break;
            }
            const [left, right] = ot_1.jot.transformX(stackActions, remoteActions);
            newStack.push(Object.assign(Object.assign({}, stack[i]), { result: Object.assign(Object.assign({}, stack[i].result), { actions: left }) }));
            remoteActions = right;
        }
        return newStack.reverse();
    }
    doTransform(remoteActions) {
        if (!remoteActions || !remoteActions.length) {
            return;
        }
        if (this._redoStack.length) {
            this._redoStack = this.transformStack(this._redoStack, remoteActions);
        }
        if (this._undoStack.length) {
            this._undoStack = this.transformStack(this._undoStack, remoteActions);
        }
    }
}
exports.UndoManager = UndoManager;
//# sourceMappingURL=undo_manager.js.map